export type RecState = 'recording' | 'paused';

/** How long the room has to stay quiet before we pause (ms). Long enough to span a normal pause between sentences. */
const SILENCE_HANG_MS = 1500;
/** Absolute floor for "someone is talking" on our 0–1 level scale. */
const MIN_SPEECH_LEVEL = 0.035;

/**
 * Records a clip with MediaRecorder and exposes a simple level meter.
 * With `skipSilence` on (default) it pauses the recorder whenever the room goes quiet for a while and
 * resumes on the next sound, so the 60 s budget is spent on speech, not on gaps. The threshold adapts
 * to the background noise level, so a busy café and a quiet kitchen both work.
 */
export class ClipRecorder {
  private stream?: MediaStream;
  private rec?: MediaRecorder;
  private chunks: Blob[] = [];
  private ctx?: AudioContext;
  private analyser?: AnalyserNode;
  private raf = 0;
  private startedAt = 0;
  /** Recorded (non-paused) time before the current run, ms. */
  private recordedBeforeMs = 0;
  private runStartedAt = 0;
  private lastSoundAt = 0;
  private noiseFloor = 0.02;
  private state: RecState = 'recording';
  mimeType = '';
  skipSilence = true;
  /** The lantern turns the phone's level-riding off, so a far voice stays far (ticket 44). Every other capture keeps it on. */
  autoGain = true;

  constructor(private onLevel: (level: number) => void, private onState?: (s: RecState) => void) {}

  static pickMime(): string {
    const cands = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
    for (const c of cands) if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c)) return c;
    return '';
  }

  async start() {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: this.autoGain, channelCount: 1 },
    });
    // Anything that throws after the microphone is open must let it go again (the tracks, the context).
    try {
      this.mimeType = ClipRecorder.pickMime();
      this.rec = new MediaRecorder(this.stream, this.mimeType ? { mimeType: this.mimeType, audioBitsPerSecond: 48_000 } : undefined);
      this.chunks = [];
      this.rec.ondataavailable = (e) => { if (e.data.size) this.chunks.push(e.data); };
      this.rec.start(250);
      this.startedAt = performance.now();
      this.runStartedAt = this.startedAt;
      this.recordedBeforeMs = 0;
      this.lastSoundAt = this.startedAt;
      this.noiseFloor = 0.02;
      this.state = 'recording';

      this.ctx = new AudioContext();
      const src = this.ctx.createMediaStreamSource(this.stream);
      this.analyser = this.ctx.createAnalyser();
      this.analyser.fftSize = 512;
      src.connect(this.analyser);
      const buf = new Uint8Array(this.analyser.fftSize);
      const tick = () => {
        this.analyser!.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / buf.length);
        const level = Math.min(1, rms * 3);
        this.onLevel(level);
        this.detectSilence(level);
        this.raf = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) {
      this.cancel();
      throw e;
    }
  }

  /** Seconds actually recorded (paused stretches excluded). */
  get elapsedSec() {
    if (!this.startedAt) return 0;
    const run = this.state === 'recording' ? performance.now() - this.runStartedAt : 0;
    return (this.recordedBeforeMs + run) / 1000;
  }

  /** Seconds skipped as silence so far. */
  get skippedSec() {
    if (!this.startedAt) return 0;
    return Math.max(0, (performance.now() - this.startedAt) / 1000 - this.elapsedSec);
  }

  get recState() { return this.state; }

  private detectSilence(level: number) {
    const now = performance.now();
    // Track the background level: follow quickly downwards, creep upwards, so speech does not raise it.
    this.noiseFloor = level < this.noiseFloor ? this.noiseFloor * 0.9 + level * 0.1 : this.noiseFloor * 0.995 + level * 0.005;
    const threshold = Math.max(MIN_SPEECH_LEVEL, this.noiseFloor * 2.5);
    const sound = level > threshold;
    if (sound) this.lastSoundAt = now;
    if (!this.skipSilence || !this.rec) { if (this.state === 'paused') this.resume(); return; }
    if (this.state === 'recording' && now - this.lastSoundAt > SILENCE_HANG_MS && this.rec.state === 'recording') {
      this.rec.pause();
      this.recordedBeforeMs += now - this.runStartedAt;
      this.state = 'paused';
      this.onState?.('paused');
    } else if (this.state === 'paused' && sound) {
      this.resume();
    }
  }

  private resume() {
    if (this.rec?.state === 'paused') this.rec.resume();
    this.runStartedAt = performance.now();
    this.state = 'recording';
    this.onState?.('recording');
  }

  async stop(): Promise<{ blob: Blob; durationSec: number }> {
    cancelAnimationFrame(this.raf);
    const durationSec = this.elapsedSec;
    const rec = this.rec;
    const blob = await new Promise<Blob>((resolve) => {
      if (!rec || rec.state === 'inactive') return resolve(new Blob(this.chunks, { type: this.mimeType || 'audio/webm' }));
      rec.onstop = () => resolve(new Blob(this.chunks, { type: rec.mimeType || this.mimeType || 'audio/webm' }));
      rec.stop();
    });
    this.cleanup();
    return { blob, durationSec };
  }

  cancel() {
    cancelAnimationFrame(this.raf);
    try { this.rec?.stop(); } catch { /* noop */ }
    this.cleanup();
  }

  private cleanup() {
    if (this.state === 'recording' && this.runStartedAt) this.recordedBeforeMs += performance.now() - this.runStartedAt;
    this.state = 'recording';
    this.startedAt = 0;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.ctx?.close().catch(() => {});
    this.stream = undefined; this.rec = undefined; this.ctx = undefined; this.analyser = undefined;
    this.onLevel(0);
  }
}
